import type { parseEnv } from './env';
import { FeatureFlag } from '../feature-flags/feature-flags';
import type { IAuthenticatedRamConfig, IPublicRamConfig, IRamRuntimeConfig } from './types';

export type IRamConfigSource = ReturnType<typeof parseEnv>;

const publicConfigKeys = [
    'SERVICE_NAME',
    'ENVIRONMENT_NAME',
    'HOUSEHOLD_VERSION',
    'S_BASECAMP_API_URL',
    'S_AI_HUB_API_URL',
    'S_MASTRA_STUDIO_URL',
    'S_PROPX_API_URL',
    'S_CALCENGINE_URL',
    'S_COCKPIT_URL',
    'FBASE_API_KEY',
    'FBASE_AUTH_DOMAIN',
    'FBASE_PROJECT_ID',
    'S_HERE_CACHE_URL',
    'JOBS_POLLING_INTERVAL_MS',
    'JOBS_PROVIDER',
    'S_ELECTRIC_URL',
    'UNLEASH_API_URL',
    'POSTHOG_HOST',
] as const satisfies readonly (keyof IPublicRamConfig)[];

const authenticatedOnlyConfigKeys = [
    'HERE_CACHE_KEY',
    'GOOGLE_MAPS_API_KEY',
    'GOOGLE_MAPS_TILES_API_KEY',
    'GOOGLE_MAPS_MAP_ID',
    'ADOBE_PDF_EMBED_CLIENT_ID',
    'EMAIL_DOCUMENT_SENDING_ADDRESSES',
    'CLASSIFIER_EMAIL',
    'ELECTRIC_USERNAME',
    'ELECTRIC_PASSWORD',
    'UNLEASH_API_TOKEN',
    'UNLEASH_API_TOKEN',
    'POSTHOG_KEY',
] as const satisfies readonly (keyof IAuthenticatedRamConfig)[];

const publicFeatureFlags = [FeatureFlag.F_USE_EMAIL_PASS_AUTH] as const;

const pickConfigFields = <TConfig extends object, TKey extends keyof TConfig>(config: TConfig, keys: readonly TKey[]): Pick<TConfig, TKey> => {
    return keys.reduce(
        (acc, key) => {
            acc[key] = config[key];
            return acc;
        },
        {} as Pick<TConfig, TKey>,
    );
};

export const buildPublicRamConfig = (config: IRamConfigSource): IPublicRamConfig => {
    return {
        IS_AUTHENTICATED_CONFIG: false,
        ...pickConfigFields(config, publicConfigKeys),
        ...pickConfigFields(config, publicFeatureFlags),
    };
};

export const buildAuthenticatedRamConfig = (config: IRamConfigSource): IAuthenticatedRamConfig => {
    return {
        ...buildPublicRamConfig(config),
        IS_AUTHENTICATED_CONFIG: true,
        ...pickConfigFields(config, authenticatedOnlyConfigKeys),
        ...pickConfigFields(config, Object.values(FeatureFlag)),
    };
};

export const isAuthenticatedRamConfig = (config: IRamRuntimeConfig | null | undefined): config is IAuthenticatedRamConfig => {
    return config?.IS_AUTHENTICATED_CONFIG === true;
};

export interface IForwardedConfigAuthHeaders {
    authorization?: string;
    cookie?: string;
}

export type IAuthenticatedConfigAccessResult =
    | {
          ok: true;
      }
    | {
          ok: false;
          status: number;
          message: string;
      };

export const getForwardedConfigAuthHeaders = (headers: Headers): IForwardedConfigAuthHeaders => {
    const authorization = headers.get('authorization') ?? undefined;
    const cookie = headers.get('cookie') ?? undefined;

    return {
        ...(authorization ? { authorization } : {}),
        ...(cookie ? { cookie } : {}),
    };
};
